import React, { memo, useCallback } from 'react';
import { formatValue } from '../../utils';

interface SliderInputProps {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (value: number) => void;
  forceDecimals?: boolean;
}

const SliderInput: React.FC<SliderInputProps> = memo(({
  id,
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
  forceDecimals = false
}) => {
  const handleSliderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(parseFloat(e.target.value));
  }, [onChange]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value) || 0;
    onChange(newValue);
  }, [onChange]);

  const handleButtonClick = useCallback((increment: number) => {
    let newValue = value + increment;
    if (String(increment).includes('.')) {
      newValue = parseFloat(newValue.toFixed(2));
    } else {
      newValue = Math.round(newValue / step) * step;
    }
    onChange(Math.max(min, Math.min(max, newValue)));
  }, [value, step, min, max, onChange]);

  // Calculate percentage for gradient background
  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div className="bg-white rounded-xl p-3 shadow-md hover:shadow-lg transition-shadow duration-200 border border-gray-100">
      <div className="mb-2">
        <div className="text-center">
          <div className="text-lg font-bold text-blue-600 whitespace-nowrap">
            {formatValue(value, unit, forceDecimals)}
          </div>
          <label htmlFor={`slider-${id}`} className="text-xs font-medium text-gray-600 block mt-1">
            {label}
          </label>
        </div>
      </div>
      
      <div className="space-y-2">
        {/* Slider with gradient track */}
        <div className="relative">
          <input
            type="range"
            id={`slider-${id}`}
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={handleSliderChange}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-track"
            style={{
              background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${percentage}%, #E5E7EB ${percentage}%, #E5E7EB 100%)`
            }}
          />
        </div>

        {/* Button controls */}
        <div className="flex items-center gap-1 justify-center">
          <div className="flex gap-1">
            <button
              type="button"
              className="w-7 h-7 flex items-center justify-center bg-gradient-to-r from-red-500 to-red-600 text-white rounded hover:from-red-600 hover:to-red-700 transition-all duration-150 shadow-sm"
              onClick={() => handleButtonClick(-step * 10)}
              title={`Decrease by ${step * 10}`}
            >
              <span className="font-bold text-xs">−−</span>
            </button>
            <button
              type="button"
              className="w-7 h-7 flex items-center justify-center bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded hover:from-orange-600 hover:to-orange-700 transition-all duration-150 shadow-sm"
              onClick={() => handleButtonClick(-step)}
              title={`Decrease by ${step}`}
            >
              <span className="font-bold text-sm">−</span>
            </button>
          </div>
          
          <input
            type="number"
            id={id}
            step={step}
            value={value}
            onChange={handleInputChange}
            className="w-20 px-1 py-1 text-center text-sm font-semibold border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all duration-150"
          />
          
          <div className="flex gap-1">
            <button
              type="button"
              className="w-7 h-7 flex items-center justify-center bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded hover:from-orange-600 hover:to-orange-700 transition-all duration-150 shadow-sm"
              onClick={() => handleButtonClick(step)}
              title={`Increase by ${step}`}
            >
              <span className="font-bold text-sm">+</span>
            </button>
            <button
              type="button"
              className="w-7 h-7 flex items-center justify-center bg-gradient-to-r from-green-500 to-green-600 text-white rounded hover:from-green-600 hover:to-green-700 transition-all duration-150 shadow-sm"
              onClick={() => handleButtonClick(step * 10)}
              title={`Increase by ${step * 10}`}
            >
              <span className="font-bold text-xs">++</span>
            </button>
          </div>
        </div>

        {/* Min/Max labels */}
        <div className="flex justify-between text-xs text-gray-400">
          <span>{formatValue(min, unit, forceDecimals)}</span>
          <span>{formatValue(max, unit, forceDecimals)}</span>
        </div>
      </div>
    </div>
  );
});

SliderInput.displayName = 'SliderInput';

export default SliderInput;